import React from "react";

import Decimal from "decimal.js";
import {produce} from "immer";

import * as constants from "@client/constants";

import * as widgets from "@client/forms/widgets";
import {useDebounce} from "@client/hooks";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {ShareLayout} from "@client/templates/Share";
import {reverse, server} from "@reactivated";

function calculateDOTS(
    _bodyWeight: DecimalValue | null,
    _liftWeight: DecimalValue | null,
    gender: string | null,
) {
    if (_bodyWeight == null || _liftWeight == null || gender == null) {
        return null;
    }

    const bodyWeight = new Decimal(_bodyWeight).toNumber();
    const liftWeight = new Decimal(_liftWeight).toNumber();

    const maleCoeff = [
        -307.75076, 24.0900756, -0.1918759221, 0.0007391293, -0.000001093,
    ];
    const femaleCoeff = [
        -57.96288, 13.6175032, -0.1126655495, 0.0005158568, -0.0000010706,
    ];

    let denominator = gender == "f" ? femaleCoeff[0] : maleCoeff[0];
    const coeff = gender == "f" ? femaleCoeff : maleCoeff;
    const maxbw = gender == "f" ? 150 : 210;
    const bw = Math.min(Math.max(bodyWeight, 40), maxbw);

    for (let i = 1; i < coeff.length; i++) {
        denominator += coeff[i] * Math.pow(bw, i);
    }

    const score = (500 / denominator) * liftWeight;
    return new Decimal(score);
}

export function Template(props: server.journal.views.Meet) {
    const isImperial = forms.useIsImperial();
    const {profile} = useSiteContext();
    const [page, setPage] = React.useState<"meet" | "rankings">("meet");

    const [participants, setParticipants] = React.useState(
        props.meet.meet_participants,
    );
    const exerciseTypes = {
        deadlift: {
            ...props.workouts.exercise_types.find(
                (exerciseType) =>
                    exerciseType.uuid == constants.EXERCISE_TYPES.DEADLIFT,
            )!,
            name: "Deadlift",
        },
        squat: {
            ...props.workouts.exercise_types.find(
                (exerciseType) => exerciseType.uuid == constants.EXERCISE_TYPES.SQUAT,
            )!,
            name: "Squat",
        },
        bench: {
            ...props.workouts.exercise_types.find(
                (exerciseType) => exerciseType.uuid == constants.EXERCISE_TYPES.BENCH,
            )!,
            name: "Bench",
        },
    };

    const dotsByUser = participants.reduce<Record<number, Decimal | null>>(
        (acc, participant) => {
            const dots = [
                exerciseTypes.deadlift,
                exerciseTypes.squat,
                exerciseTypes.bench,
            ].reduce<Decimal | null>((acc, exerciseType) => {
                const liftDots = calculateDOTS(
                    participant.weight,
                    participant.exercise_maxes.find(
                        (em) => em.exercise_type.uuid == exerciseType.uuid,
                    )?.weight ?? null,
                    participant.user.profile.gender,
                );

                if (acc == null) {
                    return liftDots;
                } else {
                    return acc.plus(liftDots ?? "0");
                }
            }, null);
            acc[participant.user.id] = dots;
            return acc;
        },
        {},
    );

    const participantsRankedByDOTS = Object.entries(dotsByUser)
        .sort((a, b) => {
            const aDots = a[1];
            const bDots = b[1];

            if (aDots == null) {
                return 1;
            }
            if (bDots == null) {
                return -1;
            }
            return bDots.minus(aDots).toNumber();
        })
        .map(([id, dots]) => ({
            participant: participants.find((p) => p.user.id == parseInt(id)),
            dots: dots?.toFixed(2) ?? "N/A",
        }));

    const initial = React.useRef(true);

    useDebounce(
        () => {
            if (props.read_only) {
                return;
            }
            if (initial.current) {
                initial.current = false;
                return;
            }
            void server.journal.views.update_meet({
                meet_participants: participants,
            });
        },
        500,
        [participants],
    );

    const content =
        page == "meet" ? (
            <table>
                <thead>
                    <tr
                        className="
                            text-left
                            *:min-w-32 *:px-2 *:pb-2
                        "
                    >
                        <th>Participant</th>
                        <th>Weight</th>
                        {[
                            exerciseTypes.squat,
                            exerciseTypes.bench,
                            exerciseTypes.deadlift,
                        ].map((exerciseType) => (
                            <th key={exerciseType.uuid}>{exerciseType.name}</th>
                        ))}
                        <th className="min-w-20">DOTS</th>
                    </tr>
                </thead>
                <tbody>
                    {participants.map((participant) => (
                        <tr
                            key={participant.user.id}
                            className="
                                text-left align-middle
                                *:px-2 *:py-4
                            "
                        >
                            <td className="text-left leading-4 whitespace-nowrap">
                                <span className="font-medium">
                                    {participant.user.profile.name} (
                                    {participant.user.profile.gender})
                                </span>
                                <br />
                                <span className="block text-xs text-gray-500">
                                    {participant.user.profile.user.email}
                                </span>
                            </td>
                            <td>
                                <widgets.WeightInput
                                    readOnly={props.read_only}
                                    isImperial={isImperial}
                                    name={`weight-${participant.user.id}`}
                                    value={participant.weight}
                                    onChange={(value) =>
                                        setParticipants(
                                            produce(participants, (draft) => {
                                                const toUpdate = draft.find(
                                                    (p) => p.uuid == participant.uuid,
                                                );
                                                if (toUpdate != null) {
                                                    toUpdate.weight = value;
                                                }
                                            }),
                                        )
                                    }
                                />
                            </td>
                            {[
                                exerciseTypes.squat,
                                exerciseTypes.bench,
                                exerciseTypes.deadlift,
                            ].map((exerciseType) => (
                                <td key={exerciseType.uuid}>
                                    <widgets.WeightInput
                                        readOnly={props.read_only}
                                        isImperial={isImperial}
                                        name={`${exerciseType.uuid}-${participant.user.id}`}
                                        value={
                                            participant.exercise_maxes.find(
                                                (em) =>
                                                    em.exercise_type.uuid ==
                                                    exerciseType.uuid,
                                            )?.weight ?? null
                                        }
                                        onChange={(value) =>
                                            setParticipants(
                                                produce(participants, (draft) => {
                                                    const toUpdate = draft.find(
                                                        (p) =>
                                                            p.uuid == participant.uuid,
                                                    );

                                                    if (toUpdate != null) {
                                                        const existing =
                                                            toUpdate.exercise_maxes.find(
                                                                (em) =>
                                                                    em.exercise_type
                                                                        .uuid ==
                                                                    exerciseType.uuid,
                                                            );

                                                        if (existing != null) {
                                                            if (value != null) {
                                                                existing.weight = value;
                                                            } else {
                                                                toUpdate.exercise_maxes =
                                                                    toUpdate.exercise_maxes.filter(
                                                                        (em) =>
                                                                            em
                                                                                .exercise_type
                                                                                .uuid !=
                                                                            exerciseType.uuid,
                                                                    );
                                                            }
                                                        } else if (value != null) {
                                                            toUpdate.exercise_maxes.push(
                                                                {
                                                                    weight: value,
                                                                    exercise_type:
                                                                        exerciseType,
                                                                },
                                                            );
                                                        }
                                                    }
                                                }),
                                            )
                                        }
                                    />
                                </td>
                            ))}
                            <td>
                                {dotsByUser[participant.user.id]?.toFixed(2) ?? "N/A"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : (
            <table className="w-full">
                <thead>
                    <tr
                        className="
                            text-left
                            *:w-1/3 *:p-2
                        "
                    >
                        <th>Rank</th>
                        <th>Participant</th>
                        <th>DOTS</th>
                    </tr>
                </thead>
                <tbody>
                    {participantsRankedByDOTS.map((participant, index) => (
                        <tr
                            key={participant.participant?.user.id}
                            className="
                                text-left
                                *:p-2
                            "
                        >
                            <td># {index + 1}</td>
                            <td>{participant.participant?.user.profile.name}</td>
                            <td>{participant.dots}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );

    const isAnonymous = profile == null;

    return (
        <ShareLayout
            share={null}
            title={props.meet.name}
            cta={
                page == "meet"
                    ? {
                          title: "Rankings",
                          onClick: () => setPage("rankings"),
                      }
                    : {
                          title: "Lifts",
                          onClick: () => setPage("meet"),
                      }
            }
        >
            {isAnonymous && (
                <div
                    className="
                        mb-4 flex items-center justify-between rounded-lg bg-blue-50 p-4
                    "
                >
                    <span className="text-blue-800">
                        Want to participate in this meet?
                    </span>
                    <button
                        className="
                            rounded-lg bg-blue-600 px-4 py-2 font-medium text-white
                            hover:bg-blue-700
                        "
                        onClick={() => {
                            window.location.href = reverse("join_meet", {
                                share_uuid: props.share_uuid,
                            });
                        }}
                    >
                        Join Meet
                    </button>
                </div>
            )}
            {content}
        </ShareLayout>
    );
}
